import { useEffect, useRef, useState } from 'react';
import { useApp } from '../context.js';
import { ratingColor } from '../lib/picker.js';
import { restaurantStats } from '../lib/visits.js';

const HEART_PATH = 'M462.3 62.6C407.5 15.9 326 24.3 275.7 76.2L256 96.5l-19.7-20.3C186.1 24.3 104.5 15.9 49.7 62.6c-62.8 53.6-66.1 149.8-9.9 207.9l193.5 199.8c12.5 12.9 32.8 12.9 45.3 0l193.5-199.8c56.3-58.1 53-154.3-9.8-207.9z';
const DARK_STYLE = [
    { elementType: 'geometry', stylers: [{ color: '#1f2937' }] },
    { elementType: 'labels.text.fill', stylers: [{ color: '#9ca3af' }] },
    { elementType: 'labels.text.stroke', stylers: [{ color: '#111827' }] },
    { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#374151' }] },
    { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#0f172a' }] },
    { featureType: 'poi', stylers: [{ visibility: 'off' }] },
];

function mapsReady() {
    return !!(window.google && window.google.maps && window.google.maps.Map);
}

function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

// "Our places" on a map: pin color = rating (green ≥ 8, amber ≥ 6, red below, gray unrated), a heart for favorites.
export default function MapView() {
    const { geocodeAddress, haptic, mutate, restaurants, setActiveTab, setExpandedId, showToast, t } = useApp();
    const el = useRef(null);
    const map = useRef(null);
    const markers = useRef([]);
    const info = useRef(null);
    const [ready, setReady] = useState(mapsReady());
    const [failed, setFailed] = useState(false);
    const [pinning, setPinning] = useState(false);

    const live = restaurants.filter(r => !r.deletedAt);
    const pinned = live.filter(r => r.coordinates && typeof r.coordinates.lat === 'number');
    const missing = live.filter(r => !(r.coordinates && typeof r.coordinates.lat === 'number') && r.address);

    // Wait for the Maps script (loaded async by index.html)
    useEffect(() => {
        if (ready) return undefined;
        const onReady = () => setReady(mapsReady());
        document.addEventListener('maps-ready', onReady);
        const giveUp = setTimeout(() => { if (!mapsReady()) setFailed(true); }, 12000);
        return () => { document.removeEventListener('maps-ready', onReady); clearTimeout(giveUp); };
    }, [ready]);

    // Open a card from a pin
    useEffect(() => {
        window.__openCardFromMap = (id) => {
            setActiveTab('restaurants');
            setExpandedId(id);
            setTimeout(() => {
                const node = document.getElementById(`restaurant-${id}`);
                if (node) node.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 150);
        };
        return () => { delete window.__openCardFromMap; };
    }, [setActiveTab, setExpandedId]);

    // Create the map once
    useEffect(() => {
        if (!ready || !el.current || map.current) return;
        const g = window.google.maps;
        const dark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
        map.current = new g.Map(el.current, {
            center: { lat: 41.8781, lng: -87.6298 },
            zoom: 11,
            disableDefaultUI: true,
            zoomControl: true,
            gestureHandling: 'greedy',
            clickableIcons: false,
            styles: dark ? DARK_STYLE : [{ featureType: 'poi', stylers: [{ visibility: 'off' }] }],
        });
        info.current = new g.InfoWindow();
    }, [ready]);

    // (Re)draw pins when the list changes
    useEffect(() => {
        if (!ready || !map.current) return;
        const g = window.google.maps;
        markers.current.forEach(m => m.setMap(null));
        markers.current = [];
        if (!pinned.length) return;
        const bounds = new g.LatLngBounds();
        for (const r of pinned) {
            const rating = restaurantStats(r).avgRating;
            const color = ratingColor(typeof rating === 'number' ? rating : null);
            const icon = r.isFavorite
                ? { path: HEART_PATH, fillColor: color, fillOpacity: 1, strokeColor: '#4a1520', strokeWeight: 1.5, scale: 0.05, anchor: new g.Point(256, 470) }
                : { path: g.SymbolPath.CIRCLE, fillColor: color, fillOpacity: 1, strokeColor: '#ffffff', strokeWeight: 2, scale: 9 };
            const marker = new g.Marker({ map: map.current, position: r.coordinates, icon, title: r.name });
            marker.addListener('click', () => {
                haptic('light');
                const ratingText = typeof rating === 'number' ? `⭐ ${rating}/10` : 'Not rated yet';
                info.current.setContent(
                    `<div style="font-family:-apple-system,system-ui,sans-serif;min-width:160px;max-width:220px">
                        <div style="font-weight:700;font-size:15px;color:#1f2937;margin-bottom:2px">${escapeHtml(r.name)}</div>
                        <div style="font-size:13px;color:#6b7280;margin-bottom:8px">${ratingText}${r.isFavorite ? ' · ❤️' : ''}</div>
                        <button onclick="window.__openCardFromMap && window.__openCardFromMap(${Number(r.id)})"
                            style="font-size:14px;font-weight:600;border:0;border-radius:10px;padding:8px 12px;background:#f97316;color:#fff">Open card ➜</button>
                    </div>`
                );
                info.current.open({ map: map.current, anchor: marker });
            });
            markers.current.push(marker);
            bounds.extend(r.coordinates);
        }
        if (pinned.length === 1) {
            map.current.setCenter(pinned[0].coordinates);
            map.current.setZoom(15);
        } else {
            map.current.fitBounds(bounds, 48);
        }
    }, [ready, restaurants]); // eslint-disable-line react-hooks/exhaustive-deps

    const addMissingPins = async () => {
        setPinning(true);
        let added = 0;
        for (const r of missing) {
            const coords = await geocodeAddress(r.address);
            if (coords) { mutate(r.id, { coordinates: coords }); added++; }
            await new Promise(res => setTimeout(res, 250)); // stay gentle with the geocoder
        }
        setPinning(false);
        showToast(added ? `Added ${added} pin${added === 1 ? '' : 's'} 📍` : "Couldn't place those addresses", added ? 'success' : 'error');
    };

    return (
        <div className="space-y-3">
            <div className="bg-white/90 apple-card rounded-2xl shadow-md overflow-hidden">
                {failed && !ready ? (
                    <div className="h-80 flex flex-col items-center justify-center text-center p-6 text-gray-500">
                        <div className="text-4xl mb-2">🗺️</div>
                        <p className="font-semibold">The map couldn't load</p>
                        <p className="text-sm mt-1">Check your connection and try again.</p>
                    </div>
                ) : (
                    <div ref={el} className="w-full" style={{ height: '60vh', minHeight: 320 }} role="region" aria-label="Map of our places">
                        {!ready && <div className="h-full flex items-center justify-center text-gray-400">Loading map…</div>}
                    </div>
                )}
            </div>
            <div className="bg-white/90 apple-card rounded-2xl shadow-md p-4">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <p className="text-sm text-gray-600">
                        {pinned.length} of {live.length} places have a pin
                    </p>
                    {missing.length > 0 && ready && (
                        <button
                            onClick={addMissingPins}
                            disabled={pinning}
                            className={`bg-gradient-to-r ${t.accent} text-white px-4 py-2 rounded-xl font-semibold text-sm apple-button disabled:opacity-60`}
                        >
                            {pinning ? 'Adding pins…' : `📍 Add ${missing.length} missing`}
                        </button>
                    )}
                </div>
                <div className="flex items-center gap-3 mt-3 text-xs text-gray-500 flex-wrap">
                    <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full inline-block" style={{ background: '#22c55e' }} /> 8+</span>
                    <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full inline-block" style={{ background: '#f59e0b' }} /> 6–8</span>
                    <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full inline-block" style={{ background: '#ef4444' }} /> under 6</span>
                    <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full inline-block" style={{ background: '#9ca3af' }} /> not rated</span>
                    <span>❤️ favorite</span>
                </div>
            </div>
        </div>
    );
}
